import React, { useEffect, useState, useMemo } from 'react';
import { Space, Typography, Row, Col, Form, Input, Tag, Button, Select } from 'antd';
import CampaignOrderQueueDealIdsDialog from './CampaignOrderQueueDealIdsDialog';
import BeaconSelector from './BeaconSelector';
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { sspPartners } from '../../../../core/utils/constants/constants';

const { Title, Text } = Typography;

export const CampaignOrderQueueReviewCampaignLaunchDetails = ({
  form,
  campaignIndex,
  lineItemIndex,
  fieldName,
  isLaunching = false,
}) => {
  const [showDealIdsDialog, setShowDealIdsDialog] = useState(false);
  const [sspOtions, setSspOptions] = useState([]);
  const campaign = form.getFieldValue('campaigns')[campaignIndex];
  const lineItem = campaign?.lineItems?.[lineItemIndex];

  const { currentCampaignOrder } = useCampaignRequestorContext();

  // Get beacons from actual campaign order data for display
  const savedBeacons = currentCampaignOrder?.campaigns?.[campaignIndex]?.lineItems?.[lineItemIndex]?.beacons;

  // Check if DSP is Nexxen and transaction tags are present
  const dspType = campaign?.demandSidePlatform;
  const hasTransactionTags = useMemo(() => {
    const transactionsConfig = currentCampaignOrder?.transactionsConfig;
    const generatedTag = transactionsConfig?.tag?.generatedTag;

    if (!generatedTag || typeof generatedTag !== 'string') {
      return false;
    }

    // Check for s2.pv or s2.tr in the generated tag
    return generatedTag.includes('s2.pv=') || generatedTag.includes('s2.tr=');
  }, [currentCampaignOrder]);

  // Check if line item has action-based bidding
  const hasActionBasedBidding = useMemo(() => {
    // This logic should match the external service's detection logic
    // You may need to adjust based on your actual bidding configuration structure
    return lineItem?.targetingType === 'ACTION_BASED' ||
           lineItem?.biddingStrategy === 'MAXIMIZE_ACTIONS';
  }, [lineItem]);

  const handleRemoveDealId = (data) => {
    const dealIds = campaign.lineItems[lineItemIndex].dealIds;
    if (!dealIds) return;
    const newDealIds = dealIds.filter((d) => d.id !== data.id);
    form.setFieldsValue({ dealIds: newDealIds });
  };

  useEffect(() => {
    setSspOptions(
      sspPartners.map((value) => ({
        value,
        label: value,
      }))
    );
  }, []);

  return (
    <>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Row>
          <Col span={12}>
            <Title level={5}>LAUNCH DETAILS</Title>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text>SSP</Text>
              <Text type="secondary">
                <small> Supply-Side Platform </small>
              </Text>
              <Form.Item
                name={[fieldName, 'supplySidePlatform']}
                rules={[
                  {
                    required: true,
                    message: 'Please select a SSP',
                  },
                ]}
              >
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  options={sspOtions}
                  disabled={['LIVE', 'PAUSED'].includes(currentCampaignOrder.campaigns[campaignIndex]?.status) || isLaunching}
                />
              </Form.Item>
            </Space>
          </Col>
          <Col span={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text>Maximum Bid</Text>
              <Text type="secondary">
                <small> Max bid per CPM 1</small>
              </Text>
              <Form.Item
                name={[fieldName, 'maxBid']}
                rules={[
                  {
                    required: true,
                    message: 'Please input a maximum bid',
                  },
                ]}
              >
                <Input
                  addonBefore="$USD"
                  type="number"
                  style={{ width: '75%' }}
                  disabled={['LIVE', 'PAUSED'].includes(currentCampaignOrder.campaigns[campaignIndex]?.status) || isLaunching}
                />
              </Form.Item>
            </Space>
          </Col>
          <Col span={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text>Frequency Cap</Text>
              <Text type="secondary">
                <small> Max daily impressions per device </small>
              </Text>
              <Form.Item
                name={[fieldName, 'frequencyCap']}
                rules={[{ required: true, message: 'Please input a frequency cap' }]}
              >
                <Input
                  type="number"
                  style={{ width: '75%' }}
                  disabled={['LIVE', 'PAUSED'].includes(currentCampaignOrder.campaigns[campaignIndex]?.status) || isLaunching}
                />
              </Form.Item>
            </Space>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text>Deal IDs</Text>
              <Text type="secondary">
                <small> Optional: Find Deal IDs to use for this campaign </small>
              </Text>
              <Space direction="horizontal" size="small" style={{ width: '100%', marginBottom: 4 }}>
                {campaign.lineItems[lineItemIndex].dealIds.map((data, index) => (
                  <Tag
                    closable={currentCampaignOrder.campaigns[campaignIndex]?.status !== 'LIVE' && !isLaunching}
                    onClose={() => handleRemoveDealId(data)}
                    key={index}
                    style={{
                      width: '100%',
                      position: 'relative',
                    }}
                    className="deal-id-tag"
                  >
                    <span
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '4px 8px',
                        width: '100%',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {data.name}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {data.dealId}
                      </span>
                    </span>
                  </Tag>
                ))}
              </Space>
              {!['LIVE', 'PAUSED'].includes(currentCampaignOrder.campaigns[campaignIndex]?.status) && !isLaunching && (
                <Button style={{ width: 200 }} onClick={() => setShowDealIdsDialog(true)}>
                  Deal ID Lookup
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        {/* Beacon Selector - Only show for Nexxen DSP with transaction tags */}
        {dspType === 'NEXXEN' && hasTransactionTags && (
          <Row>
            <Col span={24}>
              {['LIVE', 'PAUSED'].includes(currentCampaignOrder.campaigns[campaignIndex]?.status) ? (
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text>Beacons</Text>
                  <Text type="secondary">
                    <small>Attached beacons for conversion tracking</small>
                  </Text>
                  <div style={{ marginTop: 8 }}>
                    {savedBeacons && savedBeacons.length > 0 ? (
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {savedBeacons.map((beacon, index) => (
                          <div key={`${beacon.beaconId}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Tag
                              color={beacon.beaconType === 'TRANSACTION' ? 'green' :
                                     beacon.beaconType === 'PAGE_VIEW' ? 'blue' : 'default'}
                              style={{ margin: 0, minWidth: 120, textAlign: 'center' }}
                            >
                              {beacon.beaconType === 'TRANSACTION' ? 'Transaction' :
                               beacon.beaconType === 'PAGE_VIEW' ? 'Page View' :
                               beacon.beaconType || 'Standard'}
                            </Tag>
                            <Text>{beacon.name}</Text>
                          </div>
                        ))}
                      </Space>
                    ) : (
                      <Text type="secondary" style={{ fontStyle: 'italic' }}>
                        No beacons attached
                      </Text>
                    )}
                  </div>
                </Space>
              ) : (
                <Form.Item
                  name={[fieldName, 'beacons']}
                  style={{ marginBottom: 0 }}
                >
                  <BeaconSelector
                    advertiserId={currentCampaignOrder?.advertiser?.dataConfig?.dspAdvertiserId}
                    lineItemId={lineItem?.id}
                    lineItemName={lineItem?.name}
                    dspType={dspType}
                    hasTransactionTags={hasTransactionTags}
                    transactionTags={currentCampaignOrder?.transactionsConfig?.tag?.generatedTag}
                    hasActionBasedBidding={hasActionBasedBidding}
                    form={form}
                    fieldName={[fieldName, 'beacons']}
                    disabled={['LIVE', 'PAUSED'].includes(currentCampaignOrder.campaigns[campaignIndex]?.status) || isLaunching}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
        )}
      </Space>

      {showDealIdsDialog && (
        <CampaignOrderQueueDealIdsDialog
          visible={showDealIdsDialog}
          setVisible={setShowDealIdsDialog}
          selectedDealIds={form.getFieldValue('dealIds')}
          onAddSelections={(dealIds) => {
            form.setFieldsValue({
              campaigns: form.getFieldValue('campaigns').map((campaign, i) => {
                if (i === campaignIndex) {
                  return {
                    ...campaign,
                    lineItems: campaign.lineItems.map((lineItem, j) => {
                      if (j === lineItemIndex) {
                        return { ...lineItem, dealIds };
                      }
                      return lineItem;
                    }),
                  };
                }
                return campaign;
              }),
            });
            setShowDealIdsDialog(false);
          }}
        />
      )}
    </>
  );
};
